import type Store from '@ember-data/store';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import type AbilitiesService from 'ember-can/services/abilities';
import { changeset, type EmberChangeset } from 'ember-changeset';
import type IntlService from 'ember-intl/services/intl';
import WrButton from 'ember-wiser/components/button';
import Form from 'parog-web/components/treatment/form';
import type MedicationSpecialty from 'parog-web/models/medication-specialty';
import type MedicationTherapeuticalGroup from 'parog-web/models/medication-therapeutical-group';
import type Patient from 'parog-web/models/patient';
import Dosage from 'parog-web/models/prescriptions/dosage';
import Medication from 'parog-web/models/prescriptions/medication';
import MedicationRequest from 'parog-web/models/prescriptions/medication-request';
import Timing from 'parog-web/models/prescriptions/timing';
import type TreatmentPrescriptionChangedJournalEvent from 'parog-web/models/treatment-prescription-changed-journal-event';
import type ErrorsService from 'parog-web/services/errors';
import type SemeiaSessionService from 'parog-web/services/session';
import TypedEmberChangeset from 'parog-web/utils/typed-ember-changeset';
import FullscreenModal from 'ui-kit/components/fullscreen-modal';
import WrModalHeader from 'ember-wiser/components/modal/header';

/** This function is a copy of https://github.com/adopted-ember-addons/ember-changeset/blob/f85b07d9b47df08ac248b2a3ad51fabcfcb6a082/src/index.js#L263
 *
 * Just like the original function, it creates a Proxy wrapping a changeset,
 * but it also handles nested properties with a specific format
 *
 * (ex: prescriptions_0_medication for the medication property of the first prescription).
 *
 */
function NestedChangeset<T>(
  obj,
  validateFn,
  validationMap = {},
  options = {},
  store?: Store
): T {
  const c = changeset(obj, validateFn, validationMap, options);
  // Attach `store` as a plain own property on the underlying changeset
  // instance. Going through the proxy (`changeset.store = …`) would route it
  // into `_changes` via `.set()`, mutating that tracked property while the form
  // renders — which trips Glimmer's backtracking re-render assertion under
  // Embroider v4 / Vite. `store` is a service reference, not form data, so it
  // has no business being a tracked change anyway.
  if (store) {
    (c as unknown as { store: Store }).store = store;
  }
  const sep = '_';
  const nestedRegex = new RegExp(`^[A-Za-z0-9]+${sep}`);

  return new Proxy(c, {
    get(targetBuffer, key /*, receiver*/) {
      if (key.toString().match(nestedRegex)) {
        const [relationship, index, relKey] = key.toString().split(sep);
        return targetBuffer.get(relationship)[index][relKey];
      }

      return targetBuffer.get(key.toString());
    },

    set(targetBuffer, key, value /*, receiver*/) {
      if (key.toString().match(nestedRegex)) {
        const [relationship, index, relKey] = key.toString().split(sep);
        targetBuffer.get(relationship)[index][relKey] = value;
        targetBuffer.set(relationship, [...targetBuffer.get(relationship)]);
      } else {
        targetBuffer.set(key.toString(), value);
      }

      return true;
    }
  }) as T;
}

interface Signature {
  Args: {
    patient: Patient;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: [displayFormTable: () => void];
  };
}

interface prescriptionPayload extends Omit<MedicationRequest, 'medication'> {
  medication: string;
  doseQuantity?: number;
  timing?: string;
  index: number;
  isDeleted: boolean;
}

interface modelPayload {
  prescriptions: Array<prescriptionPayload>;
  occurredAt: Date;
  patient: Patient;
  recommendedTherapeuticalClasses: string[];
  notRecommendedTherapeuticalClasses: string[];
}

const journalEventProperties = [
  'occurredAt',
  'prescriptions',
  'recommendedTherapeuticalClasses',
  'notRecommendedTherapeuticalClasses'
] as const;

/**
 * Changeset used by the treatment form.
 *
 * Extends TypedEmberChangeset to:
 * - transform form data into a TreatmentPrescriptionChangedJournalEvent on save
 * - support validation of nested keys
 */
export class TreatmentFormChangeset extends TypedEmberChangeset<modelPayload> {
  declare store: Store;

  get medications(): MedicationSpecialty[] {
    return this.store.peekAll('medication-specialty').slice();
  }

  get therapeuticalClassItems(): MedicationTherapeuticalGroup[] {
    return this.store.peekAll('medication-therapeutical-group').slice();
  }

  /**
   * Transforms form data into the payload expected by TreatmentPrescriptionJournalEvent.
   *
   * - Converts prescriptions into MedicationRequest instances
   * - Converts recommendedTherapeuticalClasses into MedicationTherapeuticalGroup instances
   */
  override save =
    async (): Promise<TreatmentPrescriptionChangedJournalEvent> => {
      const payload = journalEventProperties.reduce(
        (acc, property) => {
          acc[property] = (this as EmberChangeset).get(property);
          return acc;
        },
        { patient: this.data.patient }
      ) as TreatmentPrescriptionChangedJournalEvent;
      payload.prescriptions = (
        (payload.prescriptions as unknown as prescriptionPayload[]) || []
      )
        .filter(prescription => !prescription.isDeleted)
        .map(prescription => {
          // Here we don't know if the medication is a cisCode or a custom name added by the PS
          // So we have to check if the cisCode exists in our Medications references.
          const isKnownCisCode = this.medications.find(medication => {
            return medication.cisCode === prescription.medication;
          });

          const medication = new Medication(
            isKnownCisCode ? isKnownCisCode : { name: prescription.medication }
          );

          // Create the corresponding dosage if the medic enters values.
          const doseQuantity = prescription.doseQuantity
            ? prescription.doseQuantity
            : null;
          const timing = prescription.timing
            ? Timing.fromComputedId(prescription.timing)
            : null;
          const dosage =
            doseQuantity != null || timing != null
              ? new Dosage({ doseQuantity, timing } as {
                  doseQuantity?: number;
                  timing?: Timing;
                })
              : undefined;

          return new MedicationRequest({
            medication,
            dosage,
            comment: prescription.comment,
            startDate: prescription.startDate,
            endDate: prescription.endDate,

            stoppingReason: prescription.stoppingReason,
            stoppingComment: prescription.stoppingComment
          });
        });

      const therapeuticalClassItems = this.therapeuticalClassItems;
      payload.recommendedTherapeuticalClasses = (
        payload.recommendedTherapeuticalClasses as unknown as string[]
      ).map(group =>
        therapeuticalClassItems.find(e => e.name == group)
      ) as MedicationTherapeuticalGroup[];

      payload.notRecommendedTherapeuticalClasses = (
        payload.notRecommendedTherapeuticalClasses as unknown as string[]
      ).map(group =>
        therapeuticalClassItems.find(e => e.name == group)
      ) as MedicationTherapeuticalGroup[];

      const journalEventRecord = this.store.createRecord(
        'treatment-prescription-changed-journal-event',
        payload
      );
      const newJe = await journalEventRecord.save();
      return newJe;
    };

  /**
   * Overrides `validate` to handle dynamic nested array fields.
   *
   * ember-changeset validations must be statically defined and
   * attached to known keys, which does not work for dynamic
   * indexed fields (e.g. prescriptions_0_medication).
   *
   * We manually inject per-row validation errors here before
   * delegating to the base validation to ensure proper integration
   * with the changeset error state and ember-headless-form.
   */
  override async validate(): Promise<void> {
    // start by cleaning errors
    this.removeErrors();
    (this.prescriptions as Array<prescriptionPayload>)?.forEach(
      prescription => {
        if (!prescription.isDeleted) {
          if (!prescription.medication) {
            const key = `prescriptions_${prescription.index}_medication`;
            this.addError(key, 'Le médicament ne peut être vide');
          }
          if (!!prescription.startDate && !!prescription.endDate) {
            const start = dayjs(prescription.startDate);
            const end = dayjs(prescription.endDate);
            if (end.isBefore(start, 'day')) {
              const key = `prescriptions_${prescription.index}_endDate`;
              this.addError(
                key,
                'La date de fin ne peut être antérieur à la date de début'
              );
            }
          }
        }
      }
    );
    return await super.validate();
  }
}

export default class TreatmentFormModal extends Component<Signature> {
  @service declare abilities: AbilitiesService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  @tracked declare changeset: TreatmentFormChangeset;
  @tracked shouldShowAskForPrescriptionModal = false;
  @tracked shouldShowFormModal = false;

  get originalJournalEvent():
    | TreatmentPrescriptionChangedJournalEvent
    | undefined {
    return this.args.patient.treatmentPrescriptionChangedJournalEvents[
      this.args.patient.treatmentPrescriptionChangedJournalEvents.length - 1
    ];
  }

  get #shouldShowPrescriptionButton() {
    return !(
      this.args.patient.therapeuticalArea?.id == 'mental' ||
      this.abilities.cannot('create prescription') ||
      this.session.medic?.lgcEnabled
    );
  }

  constructor(owner, args) {
    super(owner, args);

    this.#buildChangeset(this.originalJournalEvent);
  }

  closeAskForPrescriptionModal = (): void => {
    this.shouldShowAskForPrescriptionModal = false;
  };

  displayFormTable = (): void => {
    this.shouldShowFormModal = true;
  };

  onCancel = async () => {
    if (this.changeset.isDirty) {
      const result = await this.errors.showPendingChangesetError();
      if (result.value) {
        this.shouldShowFormModal = false;

        // changeset.rollback remove changes but don't reset original values
        // Rebuild the changeset as the original
        this.#buildChangeset(this.originalJournalEvent);
      }
    } else {
      this.shouldShowFormModal = false;
    }
  };

  onSubmit = async () => {
    await this.changeset.validate();
    if (this.changeset.isValid) {
      const newJe = await this.changeset.save();
      this.shouldShowFormModal = false;
      if (this.#shouldShowPrescriptionButton)
        this.shouldShowAskForPrescriptionModal = true;
      // After saving the changeset, we need to rebuild it with the new journal event to reset the changeset and reset the dirty state.
      this.#buildChangeset(newJe);
    } else {
      this.errors.displayError(
        'Veuillez corriger les erreurs avant de sauvegarder'
      );
    }
  };

  #buildChangeset = (
    je: TreatmentPrescriptionChangedJournalEvent | undefined
  ): void => {
    this.changeset = NestedChangeset<TreatmentFormChangeset>(
      this.#recordForChangeset(je),
      {},
      {},
      {
        changeset: TreatmentFormChangeset
      },
      this.store
    );
  };

  #recordForChangeset(
    je: TreatmentPrescriptionChangedJournalEvent | undefined
  ): modelPayload {
    const journalEvent = je || {};
    const model = journalEventProperties.reduce((acc, property) => {
      acc[property] = journalEvent[property];
      return acc;
    }, {});
    model['occurredAt'] = new Date();
    model['patient'] = this.args.patient;
    model['recommendedTherapeuticalClasses'] =
      (
        model[
          'recommendedTherapeuticalClasses'
        ] as MedicationTherapeuticalGroup[]
      )?.map(group => group.name) || [];
    model['notRecommendedTherapeuticalClasses'] =
      (
        model[
          'notRecommendedTherapeuticalClasses'
        ] as MedicationTherapeuticalGroup[]
      )?.map(group => group.name) || [];

    model['prescriptions'] = (
      model['prescriptions'] as MedicationRequest[]
    )?.map((prescription, i) => {
      // If the PS chose the medication from the list then the Medication has a cisCode.
      // Otherwise, if he enters an other medication himself, then the medication has only a name.
      const medication = prescription.medication.cisCode
        ? prescription.medication.cisCode
        : prescription.medication.name;

      let doseQuantity: number | undefined, timing: string | undefined;

      if (prescription.dosage) {
        doseQuantity = prescription.dosage.doseQuantity;
        timing = prescription.dosage.timing
          ? prescription.dosage.timing.computedId
          : undefined;
      }

      return {
        medication,
        doseQuantity,
        timing,
        comment: prescription.comment ?? null,
        startDate: prescription.startDate
          ? dayjs(prescription.startDate).toDate()
          : null,
        endDate: prescription.endDate
          ? dayjs(prescription.endDate).toDate()
          : null,
        stoppingReason: prescription.stoppingReason,
        stoppingComment: prescription.stoppingComment ?? null,
        index: i,
        isDeleted: false
      };
    });
    return model as modelPayload;
  }

  <template>
    {{yield this.displayFormTable}}

    {{#if this.shouldShowFormModal}}
      <FullscreenModal @onClose={{this.onCancel}}>
        <:header>
          <WrModalHeader
            class='mx-[-10px] mt-[-10px] rounded-t-[8px]'
            @onClose={{this.onCancel}}
          >Traitement</WrModalHeader>
        </:header>

        <:default>
          <Form
            @patient={{@patient}}
            @changeset={{this.changeset}}
            @journalEvent={{this.originalJournalEvent}}
            @onSubmit={{this.onSubmit}}
            @onCancel={{this.onCancel}}
          />
        </:default>
      </FullscreenModal>
    {{/if}}

    {{#if this.shouldShowAskForPrescriptionModal}}
      <FullscreenModal
        @onClose={{this.closeAskForPrescriptionModal}}
        data-test-ask-for-prescription
      >
        <p class='ui header'>
          Voulez-vous générer l'ordonnance pour ce nouveau traitement ?
        </p>

        <div class='form-buttons'>
          <WrButton
            class='ui button fluid form-button'
            @variant='link'
            {{on 'click' this.closeAskForPrescriptionModal}}
          >NON</WrButton>

          <LinkTo
            @route='medics-web.protected.patients.patient.medication-prescription.edition-form'
            @model={{@patient.id}}
            class='ui button primary fluid form-button'
            data-test-button='yes'
          >
            OUI
          </LinkTo>
        </div>
      </FullscreenModal>
    {{/if}}
  </template>
}
